import { useEffect, useMemo, useState } from "react";
import { preload } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { agentBodySrc } from "@/components/agents/agentPersonas";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { RarityBadge } from "@/components/renewal/RarityBadge";
import { CardSwap } from "@/components/renewal/fx/CardSwap";
import { GlareHover } from "@/components/renewal/fx/GlareHover";
import { ShinyText } from "@/components/renewal/fx/ShinyText";
import { cardArtSrc, cardArtSrcSet } from "@/lib/cardArt";
import { CARD_FINISHES, finishMeta } from "@/lib/cards";
import { HOME_AGENTS, type HomeAgent } from "@/lib/homeAgents";
import { SEASON_CONFIG } from "@/lib/season";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/**
 * S1 히어로 — Courtyard 배너형(pages/home.md §2): 라운드 콘텐츠 카드 안 풀블리드
 * 배너 **단일 1장, 도트/캐러셀 없음**. 좌 텍스트/CTA 블록(Inter 700, 자간 normal)
 * + 우 CardSwap 스테이지(골드 프레임). 카드 3장(비서 3인 로테이션, §2.3),
 * 최전면 GlareHover, 로드 시퀀스 §7.1 1·2·4번.
 * 시즌명 최상단 요건(§1)은 이 눈썹 `SEASON 1 — TYCOONEX`가 담당.
 */

/* Courtyard CTA — Inter 14/600, 단색 채움, radius 8px(--ty-radius-btn), 44px 타깃.
   GlowButton(mono/uppercase)은 이 배너에서 미사용 — 배너 CTA만의 로컬 스타일. */
const CTA_BASE =
  "inline-flex min-h-11 w-fit items-center justify-center rounded-(--ty-radius-btn) px-5 text-sm font-semibold transition-colors duration-200 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70";

/** §2.3 — 카드 3장 고정. 4장부터 오프셋 계단이 스테이지를 넘친다. */
const HERO_AGENTS = HOME_AGENTS.slice(0, 3);

/** 히어로 대표 피니시 — 6에이전트 전원 실카드 아트 보유 티어(Green Emerald). */
const HERO_FINISH = "blue_ice_sapphire";

/** 실카드 아트 우선, 미존재 시 기존 전신 이미지 폴백. */
function heroArt(agent: HomeAgent) {
  return cardArtSrc(agent.agentId, HERO_FINISH);
}

/* §3.1 골드 앰비언트 백드롭 — CSS만·정적(모션 예산 미소비), reduced-motion 폴백 불요 */
const AMBIENT_BACKDROP = {
  background: [
    "radial-gradient(ellipse 640px 420px at 78% 32%, rgba(217,185,97,0.10), transparent 70%)",
    "conic-gradient(from 210deg at 85% 20%, transparent 40%, rgba(217,185,97,0.05) 47%, transparent 54%)",
    "conic-gradient(from 200deg at 70% 0%, transparent 42%, rgba(184,150,63,0.04) 48%, transparent 53%)",
  ].join(","),
} as const;

function useDesktopStage() {
  const [desktop, setDesktop] = useState(
    () => window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

/** §1.4 카드 타일 4슬롯 — [아트] → [비서명+시리즈] → [보유 n/N] → [레어도 뱃지]. */
function ShowcaseCard({
  agent,
  owned,
  front,
}: {
  agent: HomeAgent;
  owned: number | null;
  front: boolean;
}) {
  const art = heroArt(agent);
  return (
    <GlareHover className="h-full w-full cursor-pointer rounded-2xl border border-(--ty-line-gold) bg-(--ty-bg-2) transition-transform duration-300 ease-(--ty-ease) hover:-translate-y-1 motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <div className="relative h-full w-full">
        {/* 실카드 아트 풀블리드 — 스테이지 5:7 vs 아트 723/1024는 ~1% 오차라 프레임
            헤더·네임플레이트가 잘리지 않는다. 미존재 시 기존 전신 이미지 렌더 유지 */}
        <img
          src={art ?? agentBodySrc(agent.name)}
          srcSet={art ? (cardArtSrcSet(agent.agentId, HERO_FINISH) ?? undefined) : undefined}
          alt={art ? `${agent.name} — ${finishMeta(HERO_FINISH).label}` : ""}
          draggable={false}
          loading={front ? undefined : "lazy"}
          fetchPriority={front ? "high" : undefined}
          className={
            art
              ? "absolute inset-0 h-full w-full object-cover"
              : "absolute inset-0 h-full w-full object-contain object-bottom px-3 pb-24 pt-3"
          }
        />
        {/* 메타 오버레이 — 아트 네임플레이트와 중복되는 이름 제거(improvements §S-1),
            뱃지는 보이는 카드 아트 피니시 고정(§S-2) — collected 수치 + 등급만 얹는다 */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-4 pb-3 pt-7">
          <p className="font-mono text-[11px] font-medium tabular-nums text-ink-dim">
            <span className="text-ink">{owned ?? "—"}</span>
            <span aria-hidden>/</span>
            {CARD_FINISHES.length} collected
          </p>
          <RarityBadge finish={HERO_FINISH} className="w-fit shrink-0" />
        </div>
      </div>
    </GlareHover>
  );
}

export function HeroShowroom() {
  const reduced = usePrefersReducedMotion();
  const desktop = useDesktopStage();
  const navigate = useNavigate();
  const { state } = useCollectionData();
  const items = state?.items ?? null;

  // §7.1 LCP — 최전면 카드 아트 preload + fetchpriority=high, 애니메이션 금지.
  // img가 1x/2x srcSet을 쓰므로 preload에도 imageSrcSet을 등록해야 DPR≥2에서
  // @2x가 preload 혜택을 받는다(1x만 등록하면 이중 다운로드).
  preload(heroArt(HERO_AGENTS[0]) ?? agentBodySrc(HERO_AGENTS[0].name), {
    as: "image",
    fetchPriority: "high",
    imageSrcSet: cardArtSrcSet(HERO_AGENTS[0].agentId, HERO_FINISH) ?? undefined,
  });

  const cardStats = useMemo(
    () =>
      HERO_AGENTS.map((agent) => {
        const mine = items?.filter((item) => item.agentId === agent.agentId) ?? null;
        return { owned: mine ? mine.filter((item) => item.owned).length : null };
      }),
    [items],
  );

  /* §3.1 좌측 텍스트 블록 — 눈썹 → 2행 헤드라인(2행째만 골드 그라디언트 클립,
     앱 내 유일 §5-6) → 보조 1줄(ShinyText 1스윕) → CTA 행(주 골드 필 + ghost) */
  const textBlock = (
    <div className="flex flex-col items-start gap-4 lg:gap-5">
      <p className="ty-eyebrow">SEASON 1 — TYCOONEX</p>
      {/* 세리프 디도네 헤드라인 (scroll-asset-prd §3, 마케팅 표면 한정) —
          uppercase · +자간 0.02em · weight 500, 2행째 골드 그라디언트 유지 */}
      <h1 className="font-serif text-[40px] font-medium uppercase leading-[1.15] tracking-[0.02em] text-ink lg:text-[48px] lg:leading-[56px]">
        Assistant cards
        <span
          className="block bg-clip-text text-transparent"
          style={{ backgroundImage: "var(--ty-gold-gradient)" }}
        >
          for Season 1
        </span>
      </h1>
      <p className="text-[15px] text-ink-dim">
        <ShinyText delay={600} duration={1800}>
          Six assistants · eight finishes · one season
        </ShinyText>
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {/* 골드 전면 필 + on-accent 잉크 — 히어로 주 CTA 1곳만 허용(§1·§5-6).
            하드 오프셋 섀도(블러 0) = Hermes 버튼 문법, 마케팅 표면 한정(§2 A5) */}
        <Link
          to="/shop"
          className={`${CTA_BASE} bg-accent text-on-accent shadow-[3px_3px_0_0_var(--ty-accent-dim)] hover:bg-accent-2`}
        >
          Open packs
        </Link>
        <Link
          to="/profile/collection"
          className={`${CTA_BASE} border border-line text-ink-dim hover:border-accent/60 hover:text-ink`}
        >
          View collection
        </Link>
      </div>
    </div>
  );

  /* §2.3 CardSwap 스테이지 — 300×420(데스크톱) / 240×336(모바일), 5:7 고정.
     구 radial 장식은 섹션 레벨 앰비언트 백드롭으로 대체(§3.1). */
  const stage = (
    <div className="relative flex h-[320px] items-center justify-center lg:h-full">
      <CardSwap
        width={desktop ? 300 : 216}
        height={desktop ? 420 : 302}
        interval={5000}
        ariaLabel="Assistant card rotation"
        cardLabels={HERO_AGENTS.map((agent) => `${agent.name} — ${SEASON_CONFIG.label}`)}
        onFrontCardClick={() => navigate("/profile/collection")}
        className="-translate-x-6 translate-y-5"
      >
        {HERO_AGENTS.map((agent, index) => (
          <ShowcaseCard
            key={agent.agentId}
            agent={agent}
            owned={cardStats[index].owned}
            front={index === 0}
          />
        ))}
      </CardSwap>
    </div>
  );

  /* Courtyard 배너형 — 라운드 콘텐츠 카드(헤어라인+bg-2) 안에서 백드롭 풀블리드.
     단일 배너, 도트 없음. 6:6 분할·overflow-hidden 유지. */
  const sectionClass =
    "relative grid min-h-[440px] grid-cols-1 items-center gap-8 overflow-hidden rounded-2xl border border-line bg-(--ty-bg-2) px-5 py-8 sm:px-8 lg:h-[560px] lg:grid-cols-[minmax(0,6fr)_minmax(0,6fr)] lg:gap-12 lg:px-12 lg:py-0";

  /* §2 A1 듀오톤 배경 아트 — 골드 선화 1점, 앰비언트 radial과 조합. 위계:
     듀오톤=배경(≤0.25 저대비), 전경 CardSwap 컬러 카드=주인공. 정적·lazy. */
  const backdrop = (
    <div aria-hidden className="pointer-events-none absolute inset-0" style={AMBIENT_BACKDROP}>
      <img
        src="/images/duotone/card-emerald.webp"
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute -right-12 top-1/2 hidden w-[440px] max-w-[55%] -translate-y-1/2 rotate-6 opacity-20 lg:block"
      />
    </div>
  );

  /* §7.4 — reduced-motion: 로드 시퀀스 없이 즉시 표시 */
  if (reduced) {
    return (
      <section aria-label="Season 1 showroom" className={sectionClass}>
        {backdrop}
        {textBlock}
        {stage}
      </section>
    );
  }

  /* §7.1 로드 시퀀스 — 1: 텍스트(0ms/600ms/y16), 2: 스테이지(120ms/700ms/y24) */
  return (
    <section aria-label="Season 1 showroom" className={sectionClass}>
      {backdrop}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {textBlock}
      </motion.div>
      <motion.div
        className="min-h-0 lg:h-full"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
      >
        {stage}
      </motion.div>
    </section>
  );
}
